import { randomId } from './id';
import type { Coords, SavedPlace } from './types';

/**
 * Gespeicherte Orte für die Schnellauswahl.
 * Bewusst nur lokal: das sind Heimatadressen, die gehören nicht auf den Server.
 */

const KEY = 'r2f.places';
const MAX_PLACES = 30;

function read(): SavedPlace[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (place): place is SavedPlace =>
        typeof place?.id === 'string' &&
        typeof place?.name === 'string' &&
        Number.isFinite(place?.lat) &&
        Number.isFinite(place?.lng),
    );
  } catch {
    return [];
  }
}

function write(places: SavedPlace[]): SavedPlace[] {
  const trimmed = places.slice(0, MAX_PLACES);
  try {
    localStorage.setItem(KEY, JSON.stringify(trimmed));
  } catch {
    /* Speicher voll oder gesperrt - die Orte sind dann nur flüchtig. */
  }
  return trimmed;
}

/** Zuletzt benutzte zuerst. */
export function listPlaces(): SavedPlace[] {
  return read().sort((a, b) => b.usedAt - a.usedAt);
}

export function savePlace(name: string, coords: Coords): SavedPlace[] {
  const now = Date.now();
  const place: SavedPlace = {
    id: randomId(),
    name: name.trim().slice(0, 40) || 'Ort ohne Namen',
    lat: coords.lat,
    lng: coords.lng,
    createdAt: now,
    usedAt: now,
  };
  return write([place, ...read()]);
}

export function renamePlace(id: string, name: string): SavedPlace[] {
  const next = read().map((place) =>
    place.id === id ? { ...place, name: name.trim().slice(0, 40) || place.name } : place,
  );
  return write(next);
}

export function deletePlace(id: string): SavedPlace[] {
  return write(read().filter((place) => place.id !== id));
}

/** Beim Auswählen nach oben sortieren, damit häufige Orte vorne stehen. */
export function touchPlace(id: string): SavedPlace[] {
  const next = read().map((place) =>
    place.id === id ? { ...place, usedAt: Date.now() } : place,
  );
  return write(next);
}

export const placeToCoords = (place: SavedPlace): Coords => ({
  lat: place.lat,
  lng: place.lng,
  label: place.name,
});
